import { useId, useState } from "react";
import * as R from "remeda";

import { StudyId, isValidStaticStudyId, studyIds } from "@shared/types";

import { Toggle } from "@shared/components/Toggle";
import { Select, SelectLabel } from "@shared/components/form/Select";

import { T } from "@/components/i18n";
import {
  MSM1a2ExportHelp,
  MSM1a2FileUploadHelp,
  MSM1a2GenericHelp,
  MSM1a3ExportHelp,
  MSM1a3FileUploadHelp,
  MSM1a3GenericHelp,
} from "@/components/help/study/msm-1a-2";

import { BookOpen } from "lucide-react";

// Types -----------------------------------------------------------------------

type StudyHelp = Record<
  StudyId,
  {
    generic: React.FC<{ showToggle: boolean }>;
    export: React.FC<{ showToggle: boolean }>;
    fileUpload: React.FC<{ showToggle: boolean; invalidFlow: boolean }>;
  }
>;

// Mappings --------------------------------------------------------------------

const studyHelp: StudyHelp = {
  "msm-1a-2": {
    generic: MSM1a2GenericHelp,
    export: MSM1a2ExportHelp,
    fileUpload: MSM1a2FileUploadHelp,
  },
  "msm-1a-3": {
    generic: MSM1a3GenericHelp,
    export: MSM1a3ExportHelp,
    fileUpload: MSM1a3FileUploadHelp,
  },
};

interface GenericHelpForStudyProps {
  studyId: StudyId;
  showToggle: boolean;
}

function GenericHelpForStudy({ showToggle, studyId }: GenericHelpForStudyProps) {
  const Component = studyHelp[studyId].generic;
  return <Component showToggle={showToggle} />;
}

interface ExportHelpForStudyProps {
  studyId: StudyId;
  showToggle: boolean;
}

function ExportHelpForStudy({ showToggle, studyId }: ExportHelpForStudyProps) {
  const Component = studyHelp[studyId].export;
  return <Component showToggle={showToggle} />;
}

interface FileUploadHelpForStudyProps {
  studyId: StudyId;
  showToggle: boolean;
  invalidFlow: boolean;
}

function FileUploadHelpForStudy({ studyId, showToggle, invalidFlow }: FileUploadHelpForStudyProps) {
  const Component = studyHelp[studyId].fileUpload;
  return <Component showToggle={showToggle} invalidFlow={invalidFlow} />;
}

// Wrapper ---------------------------------------------------------------------

function StudyHelpWrapper({
  renderHelp,
  studyId = null,
}: {
  renderHelp: (props: { studyId: StudyId; showToggle: boolean }) => React.ReactNode;
  studyId?: string | null;
}) {
  const [showHelp, setShowHelp] = useState(false);
  const [selectedStudyId, setSelectedStudyId] = useState<StudyId | null>(null);
  const selectId = useId();

  const validStaticStudyId = isValidStaticStudyId(studyId);

  if (!validStaticStudyId) {
    return (
      <div>
        <Toggle
          open={showHelp}
          onClick={() => setShowHelp((v) => !v)}
          buttonText={<T i18nKey="help.default.button" />}
        />
        {showHelp && (
          <div
            className="pt-4 pb-5 px-6 mt-6 border border-neutral-200 dark:border-neutral-800
                       rounded-xl shadow-lg"
          >
            <div className="w-fit">
              <SelectLabel htmlFor={selectId}>
                <BookOpen size={16} className="stroke-blue-500" />
                Select your study
              </SelectLabel>
              <Select
                id={selectId}
                value={selectedStudyId}
                onChange={(e) => setSelectedStudyId(e.currentTarget.value as StudyId)}
              >
                <option value="" disabled>
                  Select study
                </option>
                {R.map(studyIds, (id) => (
                  <option key={id} value={id}>
                    <T i18nKey={`study.${id}.name`} />
                  </option>
                ))}
              </Select>
            </div>
            {selectedStudyId && (
              <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
                {renderHelp({ studyId: selectedStudyId, showToggle: false })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return <div>{renderHelp({ studyId, showToggle: true })}</div>;
}

// Components  -----------------------------------------------------------------

export function StudyGenericHelp({ studyId }: { studyId?: string }) {
  return (
    <StudyHelpWrapper
      studyId={studyId}
      renderHelp={(props) => <GenericHelpForStudy {...props} />}
    />
  );
}

export function StudyExportHelp({ studyId }: { studyId?: string }) {
  return (
    <StudyHelpWrapper studyId={studyId} renderHelp={(props) => <ExportHelpForStudy {...props} />} />
  );
}

export function StudyFileUploadHelp({
  studyId,
  invalidFlow = false,
}: {
  studyId: string;
  invalidFlow?: boolean;
}) {
  return (
    <StudyHelpWrapper
      studyId={studyId}
      renderHelp={(props) => <FileUploadHelpForStudy {...props} invalidFlow={invalidFlow} />}
    />
  );
}
